import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { BsGraphUpArrow } from "react-icons/bs";
import { GrConfigure } from 'react-icons/gr';
import './ExtrapolationNode.css'; // Import the CSS file
import ExtrapolationConfigModal from './ExtrapolationConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { reconfigureNode } from '../../../api/api';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';

const ExtrapolationNode = ({ id, data, selected }) => {
  const { years, bankableYears, name, windDepPerc, solarDepPerc, bankingCharge } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [tempFormValues, setTempFormValues] = useState({});
  const reactFlowInstance = useReactFlow();
  const edges = useEdges();
  const nodes = useNodes();
  const { session, addExtrapolationNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);
  
  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`ExtrapolationNode ${id} received data:`, data);
  }, [id, data]);

  // Check if node is configured properly
  // With auto-select, derived columns are optional; treat years and bankableYears presence as sufficient
  const isConfigured = data.isConfigured || (!!years && !!bankableYears);
  
  // Check if this node has an input connection
  const hasInputConnection = edges.some(edge => edge.target === id);
  
  // Get the node mapping from context
  const { getNodeMapping } = useContext(NodeMappingContext);
  
  // Get the input node name if there's a connection
  const getInputNodeName = useCallback(() => {
    const inputEdge = edges.find(edge => edge.target === id);
    if (!inputEdge) return null;
    
    const sourceNodeId = inputEdge.source;
    const sourceNode = nodes.find(node => node.id === sourceNodeId);
    
    if (!sourceNode) return null;
    
    // First try to get the name from the node data
    if (sourceNode.data && sourceNode.data.name) {
      return sourceNode.data.name;
    }
    
    // If not found in data, try to get it from the node mapping
    const mappedName = getNodeMapping()[sourceNodeId];
    if (mappedName) {
      return mappedName;
    }
    
    // If all else fails, use the node ID
    return sourceNodeId;
  }, [edges, id, nodes, getNodeMapping]);
  
  // Get the current input node name
  const inputNodeName = getInputNodeName();
  
  // Check if the node needs reconfiguration
  // A node needs reconfiguration if:
  // 1. It's explicitly marked as needing reconfiguration (isApiConfigured === false)
  // 2. It was previously configured but now has no input connection
  // 3. It's configured but the input node has changed
  const needsReconfiguration = 
    // Explicitly marked as needing reconfiguration
    data.isApiConfigured === false || 
    // Was configured but now has no input connection
    (isConfigured && !hasInputConnection && data.inputNodeName) ||
    // Input node has changed from what was previously configured
    (isConfigured && data.inputNodeName && inputNodeName && data.inputNodeName !== inputNodeName);
  
  // Log node state for debugging
  useEffect(() => {
    console.log(`ExtrapolationNode ${id} state:`, {
      isConfigured,
      needsReconfiguration,
      name,
      years,
      bankableYears,
      hasInputConnection,
      inputNodeName,
      storedInputNodeName: data.inputNodeName,
      dataIsConfigured: data.isConfigured,
      dataIsApiConfigured: data.isApiConfigured
    });
  }, [id, isConfigured, needsReconfiguration, name, years, bankableYears, data, hasInputConnection, inputNodeName]);
  
  // Function to handle reconfiguration of the input node
  const handleReconfigureInput = async () => {
    if (!session.pipeline_name || !name) return;
    
    try {
      setIsConfiguring(true);
      
      // Get the current input node name
      const inputNodeName = getInputNodeName();
      
      if (!inputNodeName) {
        throw new Error('No input node connected');
      }
      
      console.log(`Reconfiguring node ${name} with input node ${inputNodeName}`);
      console.log(`Previous input node was: ${data.inputNodeName || 'unknown'}`);
      
      // Call the API to update the input node
      await reconfigureNode(
        session.pipeline_name,
        name,
        inputNodeName
      );
      
      console.log(`Node ${name} input reconfigured successfully from ${data.inputNodeName || 'unknown'} to ${inputNodeName}`);
      
      // Update the node data to mark it as configured
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                isConfigured: true,
                isApiConfigured: true,
                inputNodeName: inputNodeName
              }
            };
          }
          return node;
        })
      );
      
      // Also update the session state
      const position = getNodePosition();
      
      addExtrapolationNode(
        name,
        years,
        bankableYears,
        inputNodeName,
        { x: position.x, y: position.y },
        data.yoyDepPerc || '3'
      );
      
      setIsConfiguring(false);
    } catch (error) {
      console.error('Error reconfiguring node input:', error);
      notifyError(error, { operation: OP.node.reconfigure });
      setIsConfiguring(false);
    }
  };

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const openConfigModal = useCallback(() => {
    // Log the current state for debugging
    console.log("openConfigModal called with state:", {
      hasInputConnection,
      isConfigured,
      needsReconfiguration,
      years,
      bankableYears,
      isModalOpen
    });
    
    if (!hasInputConnection && !isConfigured) {
      // Show an alert if there's no input connection and we're not already configured
      // This allows editing an existing node even if the input is temporarily disconnected
      notifyError(new Error("Please connect an input to this extrapolation node before configuring it."), {
        toastId: 'extrapolation.connectInput',
      });
      return;
    }
    
    // If the node is already configured and the user is trying to edit it
    // Enable editing mode
    if (isConfigured && !needsReconfiguration) {
      console.log("Edit requested for configured node - opening edit modal");
      // Continue to open the modal in edit mode
    }
    
    // Open the full configuration modal
    console.log(`Opening modal for ${needsReconfiguration ? 'reconfiguration' : 'configuration'}`);
    
    // Force a small delay to ensure React state updates properly
    setTimeout(() => {
      setIsModalOpen(true);
      console.log("Modal should be open now:", { isModalOpen: true });
    }, 50);
  }, [hasInputConnection, isConfigured, needsReconfiguration, years, bankableYears, isModalOpen]);

  const closeConfigModal = useCallback((formValues) => {
    if (formValues && !data.isConfigured) {
      setTempFormValues(formValues);
    }
    setIsModalOpen(false);
  }, [data.isConfigured]);

  const handleConfigured = useCallback(async (configData) => {
    // Start the configuration process
    setIsConfiguring(true);
    
    try {
      const { name, years, bankableYears, windDepPerc, solarDepPerc, bankingCharge, totWindGenCol, totSolarGenCol, consAppPerc, tConsCol } = configData;
      const inputNodeName = getInputNodeName();
      const position = getNodePosition();
      
      // Log the configuration data for debugging
      console.log(`Configuring extrapolation node ${id} -> ${name}:`, {
        configData,
        inputNodeName,
        position,
        edges: edges.filter(edge => edge.target === id || edge.source === id),
        isConfigured: data.isConfigured,
        isApiConfigured: data.isApiConfigured,
        needsReconfiguration
      });
      
      // Add the extrapolation node to the session state
      if (inputNodeName) {
        // First update the node ID to name mapping before adding to session
        // This ensures the correct name is used in the pipeline state
        updateNodeMapping(id, name);
        console.log(`Updated node mapping before adding to session: ${id} -> ${name}`);
        
        // Find the source node in the nodes array to get its actual name
        const inputEdge = edges.find(edge => edge.target === id);
        const sourceNodeId = inputEdge ? inputEdge.source : null;
        const sourceNode = sourceNodeId ? nodes.find(node => node.id === sourceNodeId) : null;
        
        // Determine the actual input node name to use
        let actualInputNodeName = inputNodeName;
        
        // If the source node has a name in its data, use that
        if (sourceNode && sourceNode.data && sourceNode.data.name) {
          actualInputNodeName = sourceNode.data.name;
          console.log(`Using source node name from data: ${actualInputNodeName}`);
        }
        
        // Check if this is a reconfiguration (node already exists but input changed)
        // Only consider it a reconfiguration if needsReconfiguration is true
        // This prevents treating regular edits as reconfigurations
        const isReconfiguration = needsReconfiguration;
        
        console.log(`${isReconfiguration ? 'Reconfiguring' : 'Adding'} extrapolation node to session:`, {
          name,
          years,
          bankableYears,
          windDepPerc,
          solarDepPerc,
          bankingCharge,
          totWindGenCol,
          totSolarGenCol,
          consAppPerc,
          tConsCol,
          inputNodeName: actualInputNodeName,
          position,
          isReconfiguration
        });
        
        // Check if this is a first-time configuration, a reconfiguration, or an edit
        const isFirstTimeConfig = !data.isConfigured && !data.isApiConfigured;
        const isEditingExistingNode = data.isConfigured && !needsReconfiguration;
        
        console.log('Configuration type:', {
          isFirstTimeConfig,
          isReconfiguration,
          isEditingExistingNode,
          isConfigured: data.isConfigured,
          isApiConfigured: data.isApiConfigured,
          needsReconfiguration
        });
        
        // For first-time configuration, the modal component has already added the node to the pipeline
        if (isFirstTimeConfig && session.pipeline_name) {
          console.log(`First-time configuration for node ${name} with input ${actualInputNodeName}`);
          // No need to call the API here, it was already called by the modal component
        }
        // If this is a reconfiguration, use the reconfigure API
        else if (isReconfiguration && session.pipeline_name) {
          console.log(`Calling reconfigure API for node ${name} with new source ${actualInputNodeName}`);
          
          try {
            console.log(`Reconfiguring node ${name} with new input:`, {
              oldInput: data.inputNodeName,
              newInput: actualInputNodeName
            });
            
            // Call the API to reconfigure the node
            await reconfigureNode(
              session.pipeline_name,
              name,
              actualInputNodeName
            );
            
            console.log(`Node ${name} reconfigured successfully with new input ${actualInputNodeName}`);
          } catch (reconfigError) {
            console.error('Error reconfiguring node:', reconfigError);
            // Don't continue with local updates if API call fails
            throw reconfigError; // Re-throw the error to be caught by the outer try-catch
          }
        }
        // If this is an edit of an existing node, we don't need to call any API here
        // The editExtrapolationNode API was already called by the ExtrapolationConfigModal component
        else if (isEditingExistingNode) {
          console.log(`Editing existing node ${name} - API call already handled by modal`);
        }
        
        // Now add/update the node in the session with the correct name and all parameters
        addExtrapolationNode(
          name,
          years,
          bankableYears,
          actualInputNodeName,
          { x: position.x, y: position.y },
          windDepPerc,
          solarDepPerc,
          bankingCharge,
          totWindGenCol,
          totSolarGenCol,
          consAppPerc,
          tConsCol
        );
        
        // Update the node data with the new configuration
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  years,
                  bankableYears,
                  windDepPerc,
                  solarDepPerc,
                  bankingCharge,
                  totWindGenCol,
                  totSolarGenCol,
                  consAppPerc,
                  tConsCol,
                  isConfigured: true,
                  isApiConfigured: true,
                  inputNodeName: actualInputNodeName // Store the input node name for future reference
                }
              };
            }
            return node;
          })
        );
        
        // If the node was previously selected, update the selection with the new name
        if (session.selected_node === id) {
          console.log(`Node was selected, updating selection from ${id} to ${name}`);
          updateSelectedNode(name);
        }
      } else {
        // Just update the UI if we're not connected to a backend
        // Still update the mapping first
        updateNodeMapping(id, name);
        
        // Update the node data with the new configuration
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  years,
                  bankableYears,
                  windDepPerc,
                  solarDepPerc,
                  bankingCharge,
                  totWindGenCol,
                  totSolarGenCol,
                  consAppPerc,
                  tConsCol,
                  isConfigured: true
                }
              };
            }
            return node;
          })
        );
      }
      
      // Clear temp values and close the modal
      setTempFormValues({});
      closeConfigModal();
      
      // End the configuration process
      setIsConfiguring(false);
    } catch (error) {
      console.error('Error configuring extrapolation node:', error);
      notifyError(error, { operation: needsReconfiguration ? OP.node.reconfigure : OP.node.editExtrapolation });
      setIsConfiguring(false);
    }
  }, [id, data, getInputNodeName, getNodePosition, edges, nodes, needsReconfiguration, session, updateNodeMapping, addExtrapolationNode, reactFlowInstance, updateSelectedNode, closeConfigModal]);

  return (
    <div className={`extrapolation-node ${selected ? 'extrapolation-selected' : ''} ${!isConfigured ? 'extrapolation-not-configured' : 'extrapolation-configured'} ${needsReconfiguration ? 'extrapolation-needs-reconfiguration' : ''}`}>
      <Handle
        type="target"
        position={Position.Left}
        className={`extrapolation-connection-point ${!isConfigured ? 'extrapolation-not-configured' : 'extrapolation-configured'} ${needsReconfiguration ? 'extrapolation-needs-reconfiguration' : ''}`}
      />
      
      <div className={`extrapolation-node-header ${!isConfigured ? 'extrapolation-not-configured' : 'extrapolation-configured'} ${needsReconfiguration ? 'extrapolation-needs-reconfiguration' : ''}`}>
        <div className="extrapolation-node-header-title">
          <span className="extrapolation-node-type">Extrapolation</span>
          {name && <span className="extrapolation-node-name-display">{name}</span>}
        </div>
        
        <div className="extrapolation-node-header-icons">
          {needsReconfiguration && (
            <button 
              className="extrapolation-config-button" 
              onClick={handleReconfigureInput}
              disabled={isConfiguring || !hasInputConnection}
              title="Reconfigure input connection"
            >
              <GrConfigure className="extrapolation-icon" />
            </button>
          )}
          
          <button 
            className="extrapolation-config-button" 
            onClick={openConfigModal}
            disabled={isConfiguring}
            title={isConfigured ? "Edit configuration (Coming soon)" : "Configure node"}
          >
            {isConfigured ? <Pencil1Icon className="extrapolation-icon" /> : <ExclamationTriangleIcon className="extrapolation-icon" />}
          </button>
          
          <BsGraphUpArrow className="extrapolation-icon" />
        </div>
      </div>
      
      <div className="extrapolation-node-content">
        {!isConfigured ? (
          <div className="extrapolation-text-warning">
            <ExclamationTriangleIcon style={{ marginRight: '4px' }} />
            Not configured
          </div>
        ) : (
          <>
            <div className="extrapolation-flex-container">
              <span className="extrapolation-text-xs">Years:</span>
              <span>{years}</span>
            </div>
            <div className="extrapolation-flex-container">
              <span className="extrapolation-text-xs">Bankable Years:</span>
              <span>{bankableYears}</span>
            </div>
            <div className="extrapolation-flex-container">
              <span className="extrapolation-text-xs">Wind Dep:</span>
              <span>{windDepPerc}%</span>
            </div>
            <div className="extrapolation-flex-container">
              <span className="extrapolation-text-xs">Solar Dep:</span>
              <span>{solarDepPerc}%</span>
            </div>
            <div className="extrapolation-flex-container">
              <span className="extrapolation-text-xs">Banking Charge:</span>
              <span>{bankingCharge}%</span>
            </div>
            <div className="extrapolation-flex-container">
              <span className="extrapolation-text-xs">Cons. App:</span>
              <span>{data.consAppPerc || '0.1'}%</span>
            </div>
            {needsReconfiguration && (
              <div className="extrapolation-text-warning">
                <ExclamationTriangleIcon style={{ marginRight: '4px' }} />
                Input changed, needs reconfiguration
              </div>
            )}
          </>
        )}
      </div>
      
      <Handle
        type="source"
        position={Position.Right}
        className={`extrapolation-connection-point ${!isConfigured ? 'extrapolation-not-configured' : 'extrapolation-configured-output'} ${needsReconfiguration ? 'extrapolation-needs-reconfiguration-output' : ''}`}
      />
      
      {isModalOpen && (
        <ExtrapolationConfigModal
          key={`${id}-${isModalOpen}`}
          isOpen={isModalOpen}
          onClose={closeConfigModal}
          onConfigured={handleConfigured}
          initialData={{
            name: tempFormValues.name ?? data.name ?? '',
            years: tempFormValues.years ?? data.years ?? '',
            bankableYears: tempFormValues.bankableYears ?? data.bankableYears ?? '',
            windDepPerc: tempFormValues.windDepPerc ?? data.windDepPerc ?? '',
            solarDepPerc: tempFormValues.solarDepPerc ?? data.solarDepPerc ?? '',
            bankingCharge: tempFormValues.bankingCharge ?? data.bankingCharge ?? '',
            totWindGenCol: tempFormValues.totWindGenCol ?? data.totWindGenCol ?? '',
            totSolarGenCol: tempFormValues.totSolarGenCol ?? data.totSolarGenCol ?? '',
            consAppPerc: tempFormValues.consAppPerc ?? data.consAppPerc ?? '',
            tConsCol: tempFormValues.tConsCol ?? data.tConsCol ?? '',
            inputNodeName: inputNodeName,
            position: getNodePosition(),
            isReconfiguring: needsReconfiguration,
            isEditing: isConfigured && !needsReconfiguration
          }}
        />
      )}
    </div>
  );
};

export default ExtrapolationNode;